import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { getMessage } from "../data/locales";
import { fetchPdfArrayBuffer } from "../pdf/fetchPdfArrayBuffer";
import {
  getPdfDisplayScale,
  getPdfRenderPixelRatio,
  stepPdfZoom,
} from "../pdf/getPdfFitWidthScale";
import { isPdfRenderCancelled } from "../pdf/isPdfRenderCancelled";
import { openPdfDocument } from "../pdf/openPdfDocument";
import {
  createPdfObjectUrl,
  triggerPdfDownload,
} from "../pdf/pdfViewerActions";
import CoursePdfViewerToolbar from "./CoursePdfViewerToolbar";
import "./CourseMaterialEmbedModal.css";

/**
 * @param {{
 *   pdfDocument: import("../pdf/openPdfDocument").PdfDocument,
 *   pageNumber: number,
 *   pageCount: number,
 *   containerWidth: number,
 *   zoom: number,
 * }} props
 */
function CoursePdfPageCanvas({ pdfDocument, pageNumber, pageCount, containerWidth, zoom }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || containerWidth <= 0) {
      return undefined;
    }

    let cancelled = false;
    /** @type {import("../pdf/openPdfDocument").PdfRenderTask | null} */
    let renderTask = null;

    async function renderPage() {
      const page = await pdfDocument.getPage(pageNumber);
      if (cancelled) {
        return;
      }
      const unscaled = page.getViewport({ scale: 1 });
      const scale = getPdfDisplayScale(unscaled.width, containerWidth, zoom);
      const viewport = page.getViewport({ scale });
      const pixelRatio = getPdfRenderPixelRatio(
        typeof window !== "undefined" ? window.devicePixelRatio : 1,
      );
      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("pdf canvas context unavailable");
      }
      canvas.width = Math.floor(viewport.width * pixelRatio);
      canvas.height = Math.floor(viewport.height * pixelRatio);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      renderTask = page.render({ canvasContext: context, viewport });
      await renderTask.promise;
    }

    renderPage().catch((error) => {
      if (!cancelled && !isPdfRenderCancelled(error)) {
        console.error("Error rendering PDF page:", error);
      }
    });

    return () => {
      cancelled = true;
      if (renderTask && typeof renderTask.cancel === "function") {
        renderTask.cancel();
      }
    };
  }, [containerWidth, pageNumber, pdfDocument, zoom]);

  return (
    <canvas
      ref={canvasRef}
      className="course-material-pdf-page"
      aria-label={getMessage("campus.pdfPageLabel", { page: pageNumber, total: pageCount })}
    />
  );
}

CoursePdfPageCanvas.propTypes = {
  pdfDocument: PropTypes.shape({
    getPage: PropTypes.func.isRequired,
  }).isRequired,
  pageNumber: PropTypes.number.isRequired,
  pageCount: PropTypes.number.isRequired,
  containerWidth: PropTypes.number.isRequired,
  zoom: PropTypes.number.isRequired,
};

/**
 * @param {{ viewUrl: string, title: string, canDownload?: boolean }} props
 */
function CoursePdfDocumentViewer({ viewUrl, title, canDownload = false }) {
  const shellRef = useRef(null);
  const pdfRef = useRef(/** @type {import("../pdf/openPdfDocument").PdfDocument | null} */ (null));
  const objectUrlRef = useRef("");
  const [loadState, setLoadState] = useState("loading");
  const [pdfDocument, setPdfDocument] = useState(
    /** @type {import("../pdf/openPdfDocument").PdfDocument | null} */ (null),
  );
  const [pageCount, setPageCount] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [objectUrl, setObjectUrl] = useState("");

  useEffect(() => {
    const node = shellRef.current;
    if (!node || loadState !== "ready") {
      return undefined;
    }

    function updateWidth() {
      const nextWidth = Math.floor(node.clientWidth);
      if (nextWidth > 0) {
        setContainerWidth(nextWidth);
      }
    }

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isFullscreen, loadState]);

  useEffect(() => {
    if (!isFullscreen) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleKeyDown(event) {
      if (event.key !== "Escape") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setIsFullscreen(false);
    }

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isFullscreen]);

  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      setLoadState("loading");
      setPdfDocument(null);
      setPageCount(0);
      setContainerWidth(0);
      setZoom(1);
      setIsFullscreen(false);
      setObjectUrl("");
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = "";
      }

      try {
        const bytes = await fetchPdfArrayBuffer(viewUrl);
        const blobBytes = bytes.slice(0);
        const loaded = await openPdfDocument(bytes);
        if (cancelled) {
          if (typeof loaded.destroy === "function") {
            await loaded.destroy();
          }
          return;
        }
        if (!loaded.numPages) {
          if (typeof loaded.destroy === "function") {
            await loaded.destroy();
          }
          setLoadState("empty");
          return;
        }
        const nextObjectUrl = createPdfObjectUrl(blobBytes);
        objectUrlRef.current = nextObjectUrl;
        pdfRef.current = loaded;
        setObjectUrl(nextObjectUrl);
        setPdfDocument(loaded);
        setPageCount(loaded.numPages);
        setLoadState("ready");
      } catch (error) {
        console.error("Error loading PDF preview:", error);
        if (!cancelled) {
          setLoadState("error");
        }
      }
    }

    loadPdf();

    return () => {
      cancelled = true;
      const loaded = pdfRef.current;
      pdfRef.current = null;
      if (loaded && typeof loaded.destroy === "function") {
        loaded.destroy();
      }
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = "";
      }
    };
  }, [viewUrl]);

  if (loadState === "loading") {
    return (
      <p className="course-material-embed-status">{getMessage("campus.pdfPreviewLoading")}</p>
    );
  }

  if (loadState === "error") {
    return (
      <p className="course-material-embed-error">{getMessage("campus.pdfPreviewFailed")}</p>
    );
  }

  if (loadState === "empty" || !pdfDocument) {
    return (
      <p className="course-material-embed-status">{getMessage("campus.pdfPreviewEmpty")}</p>
    );
  }

  const pageNumbers = Array.from({ length: pageCount }, (_, index) => index + 1);
  const viewer = (
    <div
      className={`course-material-pdf-viewer${isFullscreen ? " is-fullscreen" : ""}`}
      data-zoom={String(zoom)}
    >
      <CoursePdfViewerToolbar
        zoom={zoom}
        isFullscreen={isFullscreen}
        actionsEnabled={Boolean(objectUrl)}
        canDownload={canDownload}
        onZoomOut={() => setZoom((current) => stepPdfZoom(current, -1))}
        onZoomIn={() => setZoom((current) => stepPdfZoom(current, 1))}
        onToggleFullscreen={() => setIsFullscreen((current) => !current)}
        newTabHref={canDownload ? objectUrl : ""}
        onDownload={() => {
          void triggerPdfDownload(objectUrl, title);
        }}
      />
      <div
        ref={shellRef}
        className="course-material-pdf-shell"
        role="region"
        aria-label={title}
        tabIndex={0}
      >
        <div className="course-material-pdf-pages">
          {pageNumbers.map((pageNumber) => (
            <CoursePdfPageCanvas
              key={`${viewUrl}-${pageNumber}`}
              pdfDocument={pdfDocument}
              pageNumber={pageNumber}
              pageCount={pageCount}
              containerWidth={containerWidth}
              zoom={zoom}
            />
          ))}
        </div>
      </div>
    </div>
  );

  if (isFullscreen && typeof document !== "undefined") {
    return createPortal(viewer, document.body);
  }

  return viewer;
}

CoursePdfDocumentViewer.propTypes = {
  viewUrl: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  canDownload: PropTypes.bool,
};

CoursePdfDocumentViewer.defaultProps = {
  canDownload: false,
};

export default CoursePdfDocumentViewer;
